import { useState, type FormEvent } from 'react';
import {
  OPTION_FIELD_TYPES,
  WHATSAPP_FORM_FIELD_TYPES,
  WHATSAPP_FORM_LIMITS as L,
  channelLabel,
  type WhatsappForm,
  type WhatsappFormField,
  type WhatsappFormFieldType,
  type WhatsappFormOptionSource,
} from '@neuroedge/shared';
import { Button, Card, Modal, PageState, Select, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useAuth } from '../../auth/AuthContext';
import { useChannels } from '../../api/channels';
import {
  useCreateWhatsappForm,
  useDeleteWhatsappForm,
  usePublishWhatsappForm,
  useUpdateWhatsappForm,
  useWhatsappForms,
} from '../../api/whatsapp-forms';

const muted = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' } as const;
const KEY_RE = /^[A-Za-z][A-Za-z0-9_]*$/;

const TYPE_LABEL = Object.fromEntries(WHATSAPP_FORM_FIELD_TYPES.map((t) => [t.type, t.label]));

/** "Preferred date" -> "preferred_date": the name an answer is saved under. */
export function keyFromLabel(label: string, taken: string[]): string {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/^(\d)/, 'f_$1')
      .slice(0, 30) || 'field';
  let key = base;
  for (let n = 2; taken.includes(key); n += 1) key = `${base}_${n}`;
  return key;
}

function statusOf(f: WhatsappForm): { label: string; color: string } {
  if (f.status === 'PUBLISHED' && f.hasUnpublishedChanges) {
    return { label: 'Edited - publish to update', color: 'var(--color-accent-amber)' };
  }
  if (f.status === 'PUBLISHED') return { label: 'Published', color: 'var(--color-success)' };
  if (f.status === 'FAILED') return { label: "Couldn't publish", color: 'var(--color-danger)' };
  return { label: 'Draft', color: 'var(--color-text-muted)' };
}

/**
 * WhatsApp forms (Meta "WhatsApp Flows"): a form the customer fills in
 * inside WhatsApp - one message instead of a question-and-answer exchange.
 * Build it here, publish it on a WhatsApp number, then send it from a
 * conversation flow's "Send a form" step.
 */
export function WhatsappFormsPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('automation.update');
  const forms = useWhatsappForms();
  const channels = useChannels({ type: 'WHATSAPP' });
  const publish = usePublishWhatsappForm();
  const remove = useDeleteWhatsappForm();
  const toast = useToast();
  const [editing, setEditing] = useState<WhatsappForm | 'new' | null>(null);

  if (forms.isPending) return <PageState kind="loading" message="Loading forms…" />;
  if (forms.isError)
    return (
      <PageState kind="error" message={forms.error.message} onRetry={() => void forms.refetch()} />
    );
  const channelName = (id: string) => {
    const c = channels.data?.find((x) => x.id === id);
    return c ? channelLabel(c) : 'WhatsApp number';
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 900 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}
      >
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>WhatsApp forms</h1>
        <span style={{ flex: 1 }} />
        {canManage ? <Button onClick={() => setEditing('new')}>+ New form</Button> : null}
      </div>
      <p style={{ ...muted, margin: 0 }}>
        A form opens inside WhatsApp and collects everything at once - name, address, a service, a
        date - instead of one question per message. Fewer messages means a smaller WhatsApp bill and
        a quicker customer. Publish it on your WhatsApp number (Meta needs your business to be
        verified), then add a <strong>Send a form</strong> step to a conversation flow.
      </p>

      {forms.data.length === 0 ? (
        <Card>
          <p style={{ ...muted, margin: 0 }}>No forms yet.</p>
        </Card>
      ) : (
        forms.data.map((f) => {
          const s = statusOf(f);
          return (
            <Card key={f.id}>
              <div
                role="group"
                aria-label={f.name}
                style={{ display: 'grid', gap: 'var(--space-2)' }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--space-2)',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <strong>{f.name}</strong>
                  <span className="ne-badge" style={{ borderColor: s.color, color: s.color }}>
                    {s.label}
                  </span>
                  <span style={muted}>{channelName(f.channelId)}</span>
                  <span style={{ flex: 1 }} />
                  {canManage ? (
                    <>
                      <Button variant="secondary" onClick={() => setEditing(f)}>
                        Edit
                      </Button>
                      {f.status !== 'PUBLISHED' || f.hasUnpublishedChanges ? (
                        <Button
                          disabled={publish.isPending}
                          onClick={() =>
                            publish.mutate(f.id, {
                              onSuccess: (r) =>
                                r.publishError
                                  ? toast.error(r.publishError)
                                  : toast.success(`"${r.name}" is published.`),
                              onError: (err) => toast.error(err.message),
                            })
                          }
                        >
                          {publish.isPending && publish.variables === f.id
                            ? 'Publishing…'
                            : 'Publish'}
                        </Button>
                      ) : null}
                      <Button
                        variant="secondary"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (
                            !window.confirm(
                              `Delete "${f.name}"? Flows that send it will ask its questions as text instead.`,
                            )
                          )
                            return;
                          remove.mutate(f.id, { onSuccess: () => toast.success('Form deleted.') });
                        }}
                      >
                        Delete
                      </Button>
                    </>
                  ) : null}
                </div>
                <span style={muted}>
                  {f.fields.map((x) => `${x.label}${x.required ? '' : ' (optional)'}`).join(' · ')}
                </span>
                {f.publishError ? (
                  <span role="alert" style={{ ...muted, color: 'var(--color-danger)' }}>
                    {f.publishError}
                  </span>
                ) : null}
              </div>
            </Card>
          );
        })
      )}

      {editing ? (
        <FormEditor
          form={editing === 'new' ? null : editing}
          channels={(channels.data ?? []).map((c) => ({ value: c.id, label: channelLabel(c) }))}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

type Draft = WhatsappFormField & { optionsText: string };

const blankField = (type: WhatsappFormFieldType, taken: string[]): Draft => ({
  key: keyFromLabel('field', taken),
  label: '',
  type,
  required: true,
  optionSource: OPTION_FIELD_TYPES.includes(type) ? 'STATIC' : undefined,
  optionsText: '',
});

const STARTER: Draft[] = [
  { key: 'name', label: 'Your name', type: 'TEXT', required: true, optionsText: '' },
  { key: 'phone', label: 'Phone', type: 'PHONE', required: true, optionsText: '' },
  { key: 'address', label: 'Address', type: 'LONG_TEXT', required: false, optionsText: '' },
];

function FormEditor({
  form,
  channels,
  onClose,
}: {
  form: WhatsappForm | null;
  channels: { value: string; label: string }[];
  onClose: () => void;
}) {
  const create = useCreateWhatsappForm();
  const update = useUpdateWhatsappForm();
  const toast = useToast();
  const [name, setName] = useState(form?.name ?? '');
  const [channelId, setChannelId] = useState(form?.channelId ?? channels[0]?.value ?? '');
  const [title, setTitle] = useState(form?.title ?? 'Your details');
  const [intro, setIntro] = useState(form?.intro ?? '');
  const [submitLabel, setSubmitLabel] = useState(form?.submitLabel ?? 'Submit');
  const [fields, setFields] = useState<Draft[]>(
    form ? form.fields.map((f) => ({ ...f, optionsText: (f.options ?? []).join('\n') })) : STARTER,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const patchField = (i: number, p: Partial<Draft>) =>
    setFields((all) => all.map((f, j) => (j === i ? { ...f, ...p } : f)));
  const move = (i: number, d: number) =>
    setFields((all) => {
      const j = i + d;
      if (j < 0 || j >= all.length) return all;
      const next = [...all];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });

  function payloadFields(): WhatsappFormField[] {
    return fields.map((f) => {
      const choice = OPTION_FIELD_TYPES.includes(f.type);
      return {
        key: f.key.trim(),
        label: f.label.trim(),
        type: f.type,
        required: f.required,
        ...(choice
          ? {
              optionSource: f.optionSource ?? 'STATIC',
              ...((f.optionSource ?? 'STATIC') === 'STATIC'
                ? {
                    options: f.optionsText
                      .split('\n')
                      .map((o) => o.trim())
                      .filter(Boolean),
                  }
                : {}),
            }
          : {}),
      };
    });
  }

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Enter a name.';
    if (!channelId) e.channelId = 'Choose the WhatsApp number it is published on.';
    if (!title.trim()) e.title = 'Enter a heading.';
    else if (title.trim().length > L.title) e.title = `At most ${L.title} characters.`;
    if (!submitLabel.trim()) e.submitLabel = 'Enter the button text.';
    else if (submitLabel.trim().length > L.submitLabel)
      e.submitLabel = `At most ${L.submitLabel} characters.`;
    if (fields.length === 0) e.fields = 'Add at least one field.';
    const keys = new Set<string>();
    payloadFields().forEach((f, i) => {
      if (!f.label) e[`fields/${i}/label`] = 'Enter a label.';
      else if (f.label.length > L.label) e[`fields/${i}/label`] = `At most ${L.label} characters.`;
      if (!KEY_RE.test(f.key))
        e[`fields/${i}/key`] = 'Letters, numbers and _ only, starting with a letter.';
      else if (keys.has(f.key)) e[`fields/${i}/key`] = 'Each field needs its own name.';
      keys.add(f.key);
      if (f.optionSource === 'STATIC' && !(f.options?.length ?? 0)) {
        e[`fields/${i}/options`] = 'Add at least one option, one per line.';
      } else if ((f.options?.length ?? 0) > L.staticOptions) {
        e[`fields/${i}/options`] = `At most ${L.staticOptions} options.`;
      }
    });
    return e;
  }

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) {
      setError('Please fix the highlighted fields.');
      return;
    }
    const input = {
      channelId,
      name: name.trim(),
      title: title.trim(),
      intro: intro.trim() || null,
      submitLabel: submitLabel.trim(),
      fields: payloadFields(),
    };
    try {
      if (form) await update.mutateAsync({ id: form.id, input });
      else await create.mutateAsync(input);
      toast.success(
        form ? 'Form saved - publish it to update WhatsApp.' : 'Form saved - publish it to use it.',
      );
      onClose();
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const server = fieldErrorsFromApiError(err);
        if (Object.keys(server).length) {
          setErrors(server);
          setError('Please fix the highlighted fields.');
          return;
        }
      }
      setError(err instanceof Error ? err.message : 'Could not save the form.');
    }
  }

  const busy = create.isPending || update.isPending;
  return (
    <Modal title={form ? `Edit "${form.name}"` : 'New WhatsApp form'} onClose={onClose} wide>
      <form
        onSubmit={(e) => void onSubmit(e)}
        noValidate
        style={{
          display: 'grid',
          gap: 'var(--space-4)',
          gridTemplateColumns: 'minmax(0, 1fr) 260px',
        }}
        className="wa-form-editor"
      >
        <div style={{ display: 'grid', gap: 'var(--space-3)', alignContent: 'start' }}>
          <TextField
            label="Form name (only you see it)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={errors.name}
          />
          <Select
            label="Published on"
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
            options={[{ value: '', label: 'Choose a WhatsApp number…' }, ...channels]}
            error={errors.channelId}
          />
          <TextField
            label="Heading"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={errors.title}
          />
          <TextField
            label="Text above the fields (optional)"
            value={intro}
            onChange={(e) => setIntro(e.target.value)}
            error={errors.intro}
          />

          <strong>Fields</strong>
          {errors.fields ? <span className="ne-field__error">{errors.fields}</span> : null}
          {fields.map((f, i) => {
            const choice = OPTION_FIELD_TYPES.includes(f.type);
            return (
              <div
                key={i}
                role="group"
                aria-label={`Field ${i + 1}`}
                style={{
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3)',
                  display: 'grid',
                  gap: 'var(--space-2)',
                }}
              >
                <div
                  style={{ display: 'grid', gap: 'var(--space-2)', gridTemplateColumns: '1fr 1fr' }}
                >
                  <TextField
                    label="Label"
                    value={f.label}
                    onChange={(e) => {
                      const label = e.target.value;
                      const auto = keyFromLabel(f.label, []) === f.key || f.key.startsWith('field');
                      patchField(i, {
                        label,
                        ...(auto && !form
                          ? {
                              key: keyFromLabel(
                                label,
                                fields.filter((_, j) => j !== i).map((x) => x.key),
                              ),
                            }
                          : {}),
                      });
                    }}
                    error={errors[`fields/${i}/label`]}
                  />
                  <Select
                    label="Type"
                    value={f.type}
                    onChange={(e) => {
                      const type = e.target.value as WhatsappFormFieldType;
                      patchField(i, {
                        type,
                        optionSource: OPTION_FIELD_TYPES.includes(type)
                          ? (f.optionSource ?? 'STATIC')
                          : undefined,
                      });
                    }}
                    options={WHATSAPP_FORM_FIELD_TYPES.map((t) => ({
                      value: t.type,
                      label: t.label,
                    }))}
                  />
                </div>
                <TextField
                  label="Saved as"
                  value={f.key}
                  onChange={(e) => patchField(i, { key: e.target.value })}
                  error={errors[`fields/${i}/key`]}
                />
                {choice ? (
                  <>
                    <Select
                      label="Options from"
                      value={f.optionSource ?? 'STATIC'}
                      onChange={(e) =>
                        patchField(i, { optionSource: e.target.value as WhatsappFormOptionSource })
                      }
                      options={[
                        { value: 'STATIC', label: 'Options I type' },
                        { value: 'SERVICES', label: 'My services (live)' },
                        { value: 'PRODUCTS', label: 'My products (live)' },
                      ]}
                    />
                    {(f.optionSource ?? 'STATIC') === 'STATIC' ? (
                      <div className="ne-field">
                        <label className="ne-field__label" htmlFor={`opts-${i}`}>
                          Options (one per line)
                        </label>
                        <textarea
                          id={`opts-${i}`}
                          className={[
                            'ne-input',
                            errors[`fields/${i}/options`] ? 'ne-input--error' : '',
                          ].join(' ')}
                          rows={3}
                          value={f.optionsText}
                          onChange={(e) => patchField(i, { optionsText: e.target.value })}
                        />
                        {errors[`fields/${i}/options`] ? (
                          <span className="ne-field__error" role="alert">
                            {errors[`fields/${i}/options`]}
                          </span>
                        ) : null}
                      </div>
                    ) : (
                      <span style={muted}>
                        Filled in with your current list each time the form is sent.
                      </span>
                    )}
                  </>
                ) : null}
                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--space-2)',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <label style={{ display: 'flex', gap: 'var(--space-1)', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={f.required}
                      onChange={(e) => patchField(i, { required: e.target.checked })}
                    />
                    Required
                  </label>
                  <span style={{ flex: 1 }} />
                  <Button
                    type="button"
                    variant="secondary"
                    aria-label="Move up"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                  >
                    ↑
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    aria-label="Move down"
                    disabled={i === fields.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    ↓
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setFields((all) => all.filter((_, j) => j !== i))}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            );
          })}
          {fields.length < L.fields ? (
            <div>
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  setFields((all) => [
                    ...all,
                    blankField(
                      'TEXT',
                      all.map((x) => x.key),
                    ),
                  ])
                }
              >
                + Add field
              </Button>
            </div>
          ) : null}
          <TextField
            label="Button text"
            value={submitLabel}
            onChange={(e) => setSubmitLabel(e.target.value)}
            error={errors.submitLabel}
          />
          {error ? (
            <p className="ne-auth__error" role="alert">
              {error}
            </p>
          ) : null}
          <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Save form'}
            </Button>
          </div>
        </div>

        <FormPreview title={title} intro={intro} submitLabel={submitLabel} fields={fields} />
      </form>
    </Modal>
  );
}

/** Roughly how the form looks on the customer's phone. */
function FormPreview({
  title,
  intro,
  submitLabel,
  fields,
}: {
  title: string;
  intro: string;
  submitLabel: string;
  fields: Draft[];
}) {
  return (
    <aside
      aria-label="Preview"
      style={{
        alignSelf: 'start',
        position: 'sticky',
        top: 0,
        border: '1px solid var(--color-border)',
        borderRadius: 18,
        padding: 'var(--space-3)',
        background: 'var(--color-surface-raised)',
        display: 'grid',
        gap: 'var(--space-2)',
        fontSize: 'var(--font-size-sm)',
      }}
    >
      <span style={muted}>Preview</span>
      <strong style={{ fontSize: 'var(--font-size-base)' }}>{title || 'Heading'}</strong>
      {intro ? <span>{intro}</span> : null}
      {fields.map((f, i) => (
        <div key={i} style={{ display: 'grid', gap: 2 }}>
          <span style={muted}>
            {f.label || 'Label'}
            {f.required ? '' : ' (optional)'}
          </span>
          <div
            style={{
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              padding: '6px 8px',
              color: 'var(--color-text-muted)',
              background: 'var(--color-surface)',
            }}
          >
            {TYPE_LABEL[f.type]}
            {f.type === 'DROPDOWN' || f.type === 'CHOICE' || f.type === 'CHECKBOXES'
              ? f.optionSource && f.optionSource !== 'STATIC'
                ? ` · your ${f.optionSource === 'SERVICES' ? 'services' : 'products'}`
                : f.optionsText.trim()
                  ? ` · ${f.optionsText.split('\n').filter(Boolean).length} options`
                  : ''
              : ''}
          </div>
        </div>
      ))}
      <div
        style={{
          marginTop: 'var(--space-2)',
          textAlign: 'center',
          borderRadius: 999,
          padding: '8px',
          background: 'var(--color-wa-green, var(--color-primary))',
          color: '#fff',
          fontWeight: 600,
        }}
      >
        {submitLabel || 'Submit'}
      </div>
    </aside>
  );
}
